import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Send, Check, Loader2 } from 'lucide-react';

const VARIANT_STYLES = {
  primary: {
    color: '#ffffff',
    background: '#D702F6',
    border: 'none',
    boxShadow: '0 4px 15px rgba(215, 2, 246, 0.4)'
  },
  secondary: {
    color: '#D702F6',
    background: 'transparent',
    border: '2px solid #D702F6',
    boxShadow: '0 8px 24px rgba(215, 2, 246, 0.15)'
  }
};

/**
 * Morph send→check button (Amicro-style). Paste-adapted — no Amicro package.
 */
function MorphButton({
  children = 'Submit',
  onClick,
  disabled = false,
  loading = false,
  type = 'button',
  variant = 'primary',
  iconOnly = false,
  style = {},
  className = '',
  'aria-label': ariaLabel
}) {
  const [isHovered, setIsHovered] = useState(false);
  const showHover = isHovered && !disabled && !loading;
  const label = typeof children === 'string' ? children : 'Submit';
  const variantStyle = VARIANT_STYLES[variant] || VARIANT_STYLES.primary;

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel || (iconOnly ? label : undefined)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={disabled || loading ? undefined : { scale: 1.02 }}
      whileTap={disabled || loading ? undefined : { scale: 0.96 }}
      className={className}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: iconOnly ? 0 : '0.5rem',
        width: iconOnly ? 40 : '100%',
        height: iconOnly ? 40 : 45,
        padding: iconOnly ? 0 : '0.875rem 1.25rem',
        borderRadius: iconOnly ? 8 : 8,
        fontSize: '0.875rem',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.6 : 1,
        transition: 'opacity 0.2s ease, background 0.2s ease, color 0.2s ease',
        flexShrink: 0,
        ...variantStyle,
        ...style
      }}
    >
      {loading ? (
        <Loader2 size={16} className="spin" />
      ) : (
        <div
          style={{
            position: 'relative',
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {!showHover ? (
              <motion.div
                key="icon-send"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 600, damping: 25 }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Send size={16} />
              </motion.div>
            ) : (
              <motion.div
                key="icon-check"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 600, damping: 25 }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Check size={16} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      {!iconOnly && (
        <span style={{ whiteSpace: 'nowrap' }}>{loading ? 'Sending...' : label}</span>
      )}
    </motion.button>
  );
}

export default MorphButton;
